import { computed, ref } from 'vue'
import useLocalStorageManager, {
  LS_NAMES,
} from '@/composables/localStorage/localStorageManager'
import { useProfileShow } from '@/composables/user/user'
import useAppState from '@/composables/ui/ui'

export enum ProfileMenu {
  SUBSCRIBERS = 'Subscribers',
  FOLLOWERS = 'Followers',
  ALL_POSTS = 'AllPosts',
  HIDDEN_POSTS = 'HiddenPosts',
  UNSEEN_REPLIES_POSTS = 'UnseenReplies',
  ALL_REPLIES_POSTS = 'AllReplies',
  BLACKLIST = 'Blacklist',
  REPORTED = 'Reported',
  SUPER_LIKE = 'SUPER_LIKE',
}

const USER_PROFILE_PAGE_TO_CACHE = [
  ProfileMenu.SUBSCRIBERS,
  ProfileMenu.FOLLOWERS,
  ProfileMenu.ALL_POSTS,
  ProfileMenu.HIDDEN_POSTS,
  ProfileMenu.UNSEEN_REPLIES_POSTS,
  ProfileMenu.ALL_REPLIES_POSTS,
  ProfileMenu.BLACKLIST,
  ProfileMenu.REPORTED,
  ProfileMenu.SUPER_LIKE,
]

const OTHER_USER_PAGE_TO_CACHE = [
  ProfileMenu.SUBSCRIBERS,
  ProfileMenu.FOLLOWERS,
  ProfileMenu.ALL_POSTS,
]

const { getItem, setItem } = useLocalStorageManager()
const { isShowingCurrentUser } = useProfileShow()
const activePage = ref<ProfileMenu>(ProfileMenu.ALL_POSTS)

function updateCurrentUserProfileMenuCache() {
  if (USER_PROFILE_PAGE_TO_CACHE.includes(activePage.value)) {
    setItem(LS_NAMES.DEFAULT_MY_PROFILE, activePage.value)
  }
}

function updateOtherUserProfileMenuCache() {
  if (OTHER_USER_PAGE_TO_CACHE.includes(activePage.value)) {
    setItem(LS_NAMES.DEFAULT_OTHER_PROFILE, activePage.value)
  }
}

function updateUserCache() {
  if (isShowingCurrentUser.value) {
    updateCurrentUserProfileMenuCache()
  }

  updateOtherUserProfileMenuCache()
}

function getCurrentUserDefaultMenu() {
  const cachedItem = getItem(LS_NAMES.DEFAULT_MY_PROFILE)

  if (cachedItem) {
    for (const activePostPage of USER_PROFILE_PAGE_TO_CACHE) {
      if (activePostPage === cachedItem) {
        return activePostPage
      }
    }
  }

  return ProfileMenu.ALL_POSTS
}

function getOtherUserDefaultMenu() {
  const cachedItem = getItem(LS_NAMES.DEFAULT_OTHER_PROFILE)

  if (cachedItem) {
    for (const activePostPage of OTHER_USER_PAGE_TO_CACHE) {
      if (activePostPage === cachedItem) {
        return activePostPage
      }
    }
  }

  return ProfileMenu.ALL_POSTS
}

function getProfileMenuCache() {
  if (isShowingCurrentUser.value) {
    const { query } = useAppState()
    if (query.value.showFollowers) {
      return ProfileMenu.FOLLOWERS
    }
    if (query.value.showSubscribers) {
      return ProfileMenu.SUBSCRIBERS
    }
    if (query.value.showAllReplies) {
      return ProfileMenu.ALL_REPLIES_POSTS
    }
    if (query.value.allPosts) {
      return ProfileMenu.ALL_POSTS
    }
    if (query.value.showUnseenReplies) {
      return ProfileMenu.UNSEEN_REPLIES_POSTS
    }
    if (query.value.showReportedPosts) {
      return ProfileMenu.REPORTED
    }
    if (query.value.showSuperLike) {
      return ProfileMenu.SUPER_LIKE
    }

    return getCurrentUserDefaultMenu()
  }

  return getOtherUserDefaultMenu()
}

export function useProfileMenuChange() {
  const showAll = () => {
    activePage.value = ProfileMenu.ALL_POSTS
    updateUserCache()
  }

  const showUnseenReplies = () => {
    activePage.value = ProfileMenu.UNSEEN_REPLIES_POSTS
    updateUserCache()
  }

  const showAllRepliesPosts = () => {
    activePage.value = ProfileMenu.ALL_REPLIES_POSTS
    updateUserCache()
  }

  const showSuperLike = () => {
    activePage.value = ProfileMenu.SUPER_LIKE
    updateUserCache()
  }

  const showFollowers = () => {
    activePage.value = ProfileMenu.FOLLOWERS
    updateUserCache()
  }

  const showSubscribers = () => {
    activePage.value = ProfileMenu.SUBSCRIBERS
    updateUserCache()
  }

  const showBlacklist = () => {
    activePage.value = ProfileMenu.BLACKLIST
    updateUserCache()
  }

  const showHiddenPosts = () => {
    activePage.value = ProfileMenu.HIDDEN_POSTS
    updateUserCache()
  }

  const showReported = () => {
    activePage.value = ProfileMenu.REPORTED
    updateUserCache()
  }

  return {
    showAll,
    showUnseenReplies,
    showAllRepliesPosts,
    showFollowers,
    showReported,
    showSubscribers,
    showBlacklist,
    showHiddenPosts,
    showSuperLike,
  }
}

export default function useProfileMenu() {
  const isAllPosts = computed(() => {
    return activePage.value === ProfileMenu.ALL_POSTS
  })

  const isHiddenPosts = computed(() => {
    return activePage.value === ProfileMenu.HIDDEN_POSTS
  })

  const isReported = computed(() => {
    return activePage.value === ProfileMenu.REPORTED
  })

  const isBlacklist = computed(() => {
    return activePage.value === ProfileMenu.BLACKLIST
  })

  const isAllRepliesPosts = computed(() => {
    return activePage.value === ProfileMenu.ALL_REPLIES_POSTS
  })

  const isUnseenRepliesPosts = computed(() => {
    return activePage.value === ProfileMenu.UNSEEN_REPLIES_POSTS
  })

  const isSubscribers = computed(() => {
    return activePage.value === ProfileMenu.SUBSCRIBERS
  })

  const isFollowers = computed(() => {
    return activePage.value === ProfileMenu.FOLLOWERS
  })

  const isSuperLike = computed(() => {
    return activePage.value === ProfileMenu.SUPER_LIKE
  })

  const init = () => {
    activePage.value = getProfileMenuCache()
  }

  const isMenuPostsActive = computed(() => {
    return (
      activePage.value === ProfileMenu.ALL_POSTS ||
      activePage.value === ProfileMenu.HIDDEN_POSTS
    )
  })

  const isMenuReactionsActive = computed(() => {
    return (
      activePage.value === ProfileMenu.UNSEEN_REPLIES_POSTS ||
      activePage.value === ProfileMenu.ALL_REPLIES_POSTS
    )
  })

  return {
    activePage,
    isAllPosts,
    isHiddenPosts,
    isAllRepliesPosts,
    isUnseenRepliesPosts,
    isSubscribers,
    isFollowers,
    isMenuPostsActive,
    isMenuReactionsActive,
    isBlacklist,
    isReported,
    isSuperLike,
    init,
  }
}
